import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import PostCard from '../components/PostCard';
import CreatePostDialog from '../components/CreatePostDialog';
import RightSidebar from '../components/RightSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Loader2, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, page, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/api/posts?${params}`);
      setPosts(response.data.posts);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
    setShowCreatePost(false);
  };

  const handleLike = async (postId) => {
    try {
      const response = await axios.post(`${API}/api/posts/${postId}/like`);
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, likes_count: response.data.likes_count }
          : post
      ));
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      <Header />
      
      <div className="content-container pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-8">
          {/* Left Sidebar */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24">
              <Sidebar 
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={(cat) => {
                  setSelectedCategory(cat);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Main Feed */}
          <div className="lg:col-span-6">
            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  className="pl-11 rounded-2xl bg-white border-stone-200 h-12"
                  data-testid="search-posts-input"
                />
              </div>
            </div>

            {/* Create Post Button */}
            <div className="mb-6">
              <Button
                onClick={() => setShowCreatePost(true)}
                className="w-full bg-white hover:bg-stone-50 text-stone-600 border border-stone-200 rounded-2xl h-14 justify-start px-6 font-normal"
                data-testid="create-post-button"
              >
                <Plus className="h-5 w-5 mr-3 text-[#6B8E7B]" />
                Share something with the community...
              </Button>
            </div>

            {/* Mobile Category Pills */}
            <div className="flex gap-2 overflow-x-auto pb-4 lg:hidden mb-4">
              <button
                onClick={() => { setSelectedCategory(null); setPage(1); }}
                className={`category-pill whitespace-nowrap ${!selectedCategory ? 'active' : ''}`}
                data-testid="category-all-mobile"
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => { setSelectedCategory(cat.id); setPage(1); }}
                  className={`category-pill whitespace-nowrap ${selectedCategory === cat.id ? 'active' : ''}`}
                  data-testid={`category-${cat.id}-mobile`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Posts */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#6B8E7B]" />
              </div>
            ) : posts.length === 0 ? (
              <div className="empty-state bg-white rounded-2xl border border-stone-200 p-8">
                <div className="empty-state-icon">
                  <Plus className="h-16 w-16" />
                </div>
                <h3 className="text-xl font-semibold text-stone-800 mb-2">No posts yet</h3>
                <p className="text-stone-500 mb-4">Be the first to share something with the community!</p>
                <Button
                  onClick={() => setShowCreatePost(true)}
                  className="btn-primary"
                  data-testid="create-first-post-button"
                >
                  Create a post
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post, index) => (
                  <div key={post.id} style={{ animationDelay: `${index * 50}ms` }} className="animate-fade-in">
                    <PostCard post={post} onLike={handleLike} />
                  </div>
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-full"
                      data-testid="prev-page-button"
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-4 text-stone-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="rounded-full"
                      data-testid="next-page-button"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-24">
              <RightSidebar />
            </div>
          </div>
        </div>
      </div>

      <CreatePostDialog
        open={showCreatePost}
        onClose={() => setShowCreatePost(false)}
        onPostCreated={handlePostCreated}
        categories={categories}
      />
    </div>
  );
};

export default HomePage;
